import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "motion/react";
import { Menu, X, Phone } from "lucide-react";
import { siteData } from "../data/mock";

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { label: "About", href: "#about" },
    { label: "Services", href: "#services" },
    { label: "Testimonials", href: "#testimonials" },
    { label: "Contact", href: "#contact" },
  ];

  const scrollTo = (href) => {
    setIsMobileMenuOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      role="banner"
      className={`fixed top-0 left-0 right-0 z-[999] transition-shadow duration-300 ${
        isScrolled
          ? "shadow-sm"
          : ""
      }`}
      style={{
        background: isScrolled
          ? "rgba(255, 249, 242, 0.95)"
          : "transparent",
        backdropFilter: isScrolled ? "blur(8px)" : "none",
        WebkitBackdropFilter: isScrolled ? "blur(8px)" : "none",
      }}
    >
      <div className="max-w-[1280px] mx-auto px-5 md:px-9 flex items-center justify-between h-20">
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
          className="flex items-center gap-2 no-underline"
        >
          <span
            className="text-[#232323] text-xl tracking-tight"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}
          >
            Young Vitality Acupuncture
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-8" aria-label="Main navigation">
          {navLinks.map((link) => (
            <button
              key={link.label}
              onClick={() => scrollTo(link.href)}
              className="bg-transparent border-none text-[#353535] text-sm font-medium cursor-pointer hover:text-[#232323] transition-colors duration-200"
              style={{ fontFamily: "'Inter', sans-serif" }}
              aria-label={`Navigate to ${link.label} section`}
            >
              {link.label}
            </button>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <a
            href={siteData.clinic.phoneLink}
            className="inline-flex items-center gap-2 text-[#353535] text-sm font-medium no-underline hover:text-[#232323] transition-colors duration-200"
          >
            <Phone size={15} strokeWidth={1.5} />
            <span className="font-mono text-xs">{siteData.clinic.phone}</span>
          </a>
          <button
            onClick={() => scrollTo("#contact")}
            className="bg-[#232323] text-white border-none rounded-full px-5 py-2.5 font-mono text-xs font-medium uppercase tracking-wide cursor-pointer hover:bg-[#353535] hover:scale-[1.02] active:scale-[0.98] transition-transform duration-200"
          >
            Book Now
          </button>
        </div>

        <button
          className="lg:hidden bg-transparent border-none p-2 cursor-pointer text-[#232323]"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label={isMobileMenuOpen ? "Close navigation menu" : "Open navigation menu"}
          aria-expanded={isMobileMenuOpen}
          aria-controls="mobile-nav-menu"
        >
          {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden overflow-hidden"
            id="mobile-nav-menu"
            role="navigation"
            aria-label="Mobile navigation"
            style={{
              background: "rgba(255, 249, 242, 0.98)",
              backdropFilter: "blur(12px)",
              borderTop: "1px solid rgba(153, 153, 153, 0.2)",
            }}
          >
            <div className="px-5 py-6 flex flex-col gap-1">
              {navLinks.map((link) => (
                <button
                  key={link.label}
                  onClick={() => scrollTo(link.href)}
                  className="bg-transparent border-none text-left text-[#353535] text-base font-medium py-3 cursor-pointer hover:text-[#232323]"
                >
                  {link.label}
                </button>
              ))}
              <div className="h-px bg-[rgba(153,153,153,0.2)] my-2" />
              <a
                href={siteData.clinic.phoneLink}
                className="inline-flex items-center gap-2 text-[#353535] text-base font-medium py-3 no-underline"
              >
                <Phone size={18} strokeWidth={1.5} />
                {siteData.clinic.phone}
              </a>
              <button
                onClick={() => scrollTo("#contact")}
                className="bg-[#232323] text-white border-none rounded-full px-5 py-3.5 font-mono text-sm font-medium uppercase tracking-wide cursor-pointer w-full mt-2"
              >
                Book an Appointment
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
